import MuiAutocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteValue,
  AutocompleteProps as MuiAutocompleteProps,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import React, { useCallback, useMemo } from 'react';

export type AutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
> = MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

export const Autocomplete = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>({
  options,
  value,
  onChange,
  ...props
}: Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'options' | 'value' | 'onChange'
> & {
  options: readonly (T | Partial<T> | { [K in keyof T]?: T[K] | null })[];
  value?:
    | AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
    | Partial<T>
    | { [K in keyof T]?: T[K] | null };
  onChange?: (...args: any[]) => void;
}) => {
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Stop propagation to prevent parent components (like Menu) from capturing keypresses
    event.stopPropagation();
  }, []);

  return (
    <MuiAutocomplete
      options={options as readonly T[]}
      value={
        value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>
      }
      onChange={
        onChange as MuiAutocompleteProps<
          T,
          Multiple,
          DisableClearable,
          FreeSolo
        >['onChange']
      }
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

type AddableAutocompleteProps<
  T extends { title: string; inputValue?: string; selectedTitle?: string },
  Multiple extends boolean = false,
  DisableClearable extends boolean = false
> = AutocompleteProps<T, Multiple, DisableClearable, true> & {
  createOnEnter?: boolean;
  isExistingOption?: (option: T, inputValue: string) => boolean;
  onAddItem?: (inputValue: string) => T | void | Promise<T | void>;
};

export const AddableAutocomplete = <
  T extends {
    title: string;
    inputValue?: string;
    selectedTitle?: string;
    id?: number | string;
  },
  Multiple extends boolean = false,
  DisableClearable extends boolean = false
>({
  isExistingOption,
  onAddItem,
  onChange,
  createOnEnter = true,
  ...props
}: AddableAutocompleteProps<T, Multiple, DisableClearable>) => {
  const filter = useMemo(() => createFilterOptions<T>(), []);

  const handleChange = useCallback(
    async (
      event: React.SyntheticEvent,
      value: AutocompleteValue<T, Multiple, DisableClearable, true>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<T>
    ) => {
      const isEnterKey =
        event.type === 'keydown' &&
        (event as React.KeyboardEvent).key === 'Enter';
      // Handle the "Add" option
      const isAddedOption = (option: unknown): option is T =>
        typeof option === 'object' &&
        option !== null &&
        'inputValue' in option &&
        typeof (option as T).inputValue === 'string' &&
        !!(option as T).inputValue;

      const addedOptions: T[] = [];
      const addedInputValues: string[] = [];
      const addInputValue = (inputValue: string) => {
        if (!addedInputValues.includes(inputValue)) {
          addedInputValues.push(inputValue);
        }
      };

      if (isAddedOption(details?.option)) {
        addedOptions.push(details.option);
      } else if (Array.isArray(value)) {
        value.forEach((option) => {
          if (isAddedOption(option)) {
            addedOptions.push(option);
          }
        });
      } else if (isAddedOption(value)) {
        addedOptions.push(value);
      }

      // When disabled, Enter may report either createOption or selectOption
      // for the highlighted Add option. Existing options remain selectable.
      if (
        !createOnEnter &&
        isEnterKey &&
        (reason === 'createOption' || addedOptions.length)
      ) {
        return;
      }

      if (reason === 'createOption' && onAddItem) {
        if (Array.isArray(value)) {
          value.forEach((option) => {
            if (typeof option === 'string' && option) {
              addInputValue(option);
            }
          });
        } else if (typeof value === 'string' && value) {
          addInputValue(value);
        }
      }

      addedOptions.forEach((option) => {
        addInputValue(option.inputValue as string);
      });

      if (addedInputValues.length) {
        try {
          const createdOptions = new Map<T, T>();
          const createdOptionsByInputValue = new Map<string, T>();
          for (const inputValue of addedInputValues) {
            const createdOption = await onAddItem?.(inputValue);
            if (createdOption) {
              createdOptionsByInputValue.set(inputValue, createdOption);
              const addedOption = addedOptions.find(
                (option) => option.inputValue === inputValue
              );
              if (addedOption) {
                createdOptions.set(addedOption, createdOption);
              }
            }
          }

          if (createdOptions.size || createdOptionsByInputValue.size) {
            const nextValue = Array.isArray(value)
              ? value.map((option) =>
                  typeof option === 'string'
                    ? createdOptionsByInputValue.get(option) ?? option
                    : createdOptions.get(option) ?? option
                )
              : typeof value === 'string'
                ? createdOptionsByInputValue.get(value) ?? value
                : (createdOptions.get(value as T) ?? value);
            const createdDetailsOption =
              details && createdOptions.get(details.option);

            onChange?.(
              event,
              nextValue as AutocompleteValue<
                T,
                Multiple,
                DisableClearable,
                true
              >,
              reason,
              createdDetailsOption
                ? { ...details, option: createdDetailsOption }
                : details
            );
            return;
          }
        } catch {
          return;
        }
      }
      onChange?.(event, value, reason, details);
    },
    [createOnEnter, onAddItem, onChange]
  );

  const filterOptions = useCallback(
    (options: readonly T[], params: any) => {
      const filtered = filter([...options], {
        ...params,
        getOptionLabel: (option: T) => option.title,
      });

      const { inputValue } = params;
      const isExisting = options.some(
        (option) =>
          isExistingOption?.(option, inputValue) ??
          (inputValue === option.title ||
            inputValue === option.selectedTitle)
      );
      if (inputValue !== '' && !isExisting) {
        filtered.push({
          inputValue,
          title: `Add "${inputValue}"`,
        } as T);
      }
      return filtered;
    },
    [filter, isExistingOption]
  );

  const getOptionLabel = useCallback((option: T | string) => {
    if (!option) return '';
    if (typeof option === 'string') {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.selectedTitle ?? option.title;
  }, []);

  const renderOption = useCallback((props: any, option: T) => {
    const { key, ...optionProps } = props;
    // Create a stable key - use option.id if available, otherwise use inputValue for "Add" options
    const stableKey = option.id
      ? `${key}-${option.id}`
      : option.inputValue
      ? `${key}-add-${option.inputValue}`
      : key;

    return (
      <li key={stableKey} {...optionProps}>
        {option.title}
      </li>
    );
  }, []);

  return (
    <Autocomplete
      {...props}
      onChange={handleChange}
      freeSolo
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
    />
  );
};
